import { parse } from 'node-html-parser'

import type { GhostHeading } from '~/types'
import type { RvUnifiedSearchQuery } from '~/types/rental-api-aliases'

import { formatAsSlug, normalizeAttributeId } from '~/utils/formatting/format-strings'

const H2_HEADING_REGEX = /<h2\b([^>]*)>([\s\S]*?)<\/h2>/gi
const HEADING_OPEN_TAG_REGEX = /<(h[1-6])\b([^>]*)>/gi
const HEADING_ID_ATTR_REGEX = /\bid=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i
const HASH_HREF_REGEX = /(<a\b[^>]*\bhref=)(["'])#([^"']*)\2/gi
const NUMERIC_QUERY_VALUE_REGEX = /^-?\d+(\.\d+)?$/
const POPULAR_RVS_SCRIPT_SRC = 'popular-rvs.iife.js'

function extractHeadingText(innerHtml: string): string {
  return innerHtml.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

function allocateUniqueHeadingId(baseId: string, usedIds: Map<string, number>): string {
  if (!usedIds.has(baseId)) {
    usedIds.set(baseId, 0)
    return baseId
  }

  const next = usedIds.get(baseId)! + 1
  usedIds.set(baseId, next)

  return `${baseId}-${next}`
}

/**
 * Adds id attributes to h2 elements that lack them so in-page anchor links work.
 *
 * @param html The HTML content to update.
 * @returns The HTML with ids on all h2 headings that have visible text.
 *
 * @example ensureH2HeadingIds('<h2>Introduction</h2>')
 */
export function ensureH2HeadingIds(html: string): string {
  const usedIds = new Map<string, number>()

  return html.replace(H2_HEADING_REGEX, (match, attrs: string, innerHtml: string) => {
    const text = extractHeadingText(innerHtml ?? '')
    if (!text) {
      return match
    }

    const idMatch = new RegExp(HEADING_ID_ATTR_REGEX).exec(attrs)
    const rawId = idMatch?.[1] ?? idMatch?.[2] ?? idMatch?.[3]
    const baseId = rawId ? normalizeAttributeId(rawId) : (formatAsSlug(text) || 'section')
    const id = allocateUniqueHeadingId(baseId, usedIds)

    let newAttrs: string
    if (idMatch) {
      newAttrs = attrs.replace(HEADING_ID_ATTR_REGEX, `id="${id}"`)
    }
    else if (attrs) {
      newAttrs = `${attrs} id="${id}"`
    }
    else {
      newAttrs = ` id="${id}"`
    }

    return `<h2${newAttrs}>${innerHtml}</h2>`
  })
}

/**
 * Normalizes heading ids and in-page anchor hrefs in Ghost HTML.
 *
 * @param html The HTML content to normalize.
 * @returns The HTML with normalized heading ids and hash links.
 *
 * @example normalizeHtmlAttributeIds('<h2 id="caf%C3%A9">Café</h2>')
 */
export function normalizeHtmlAttributeIds(html: string): string {
  const normalizedHtml = html.replace(HEADING_OPEN_TAG_REGEX, (match, tag: string, attrs: string) => {
    const idMatch = new RegExp(HEADING_ID_ATTR_REGEX).exec(attrs)
    if (!idMatch) {
      return match
    }

    const rawId = idMatch[1] ?? idMatch[2] ?? idMatch[3]
    if (!rawId) {
      return match
    }

    const normalizedId = normalizeAttributeId(rawId)
    const newAttrs = attrs.replace(HEADING_ID_ATTR_REGEX, `id="${normalizedId}"`)

    return `<${tag}${newAttrs}>`
  })

  return normalizedHtml.replace(
    HASH_HREF_REGEX,
    (_match, prefix: string, quote: string, hash: string) =>
      `${prefix}${quote}#${normalizeAttributeId(hash)}${quote}`,
  )
}

/**
 * Normalizes Ghost HTML and extracts h2 headings for in-page navigation.
 *
 * @param html The HTML content to prepare.
 * @returns The normalized HTML and extracted headings.
 *
 * @example prepareGhostContentHtml('<h2>Introduction</h2>')
 */
export function prepareGhostContentHtml(html: string): { html: string, headings: GhostHeading[] } {
  const normalizedHtml = ensureH2HeadingIds(normalizeHtmlAttributeIds(html))

  return {
    html: normalizedHtml,
    headings: getH2HeadingsFromHtml(normalizedHtml),
  }
}

/**
 * Strips leftover popular-rvs IIFE script tags from Ghost HTML cards.
 *
 * @param html The Ghost HTML content to clean.
 * @returns The HTML without popular-rvs IIFE scripts.
 *
 * @example stripPopularRvsScripts('<script src="https://rvezy.tools/popular-rvs/popular-rvs.iife.js"></script><popular-rvs></popular-rvs>')
 */
export function stripPopularRvsScripts(html: string): string {
  const root = parse(html, { comment: true })

  for (const script of root.querySelectorAll('script')) {
    if (script.getAttribute('src')?.includes(POPULAR_RVS_SCRIPT_SRC)) {
      script.remove()
    }
  }

  return root.toString()
}

function coercePopularRvsQueryValue(value: string): boolean | number | string {
  const normalized = value.trim()
  const lowercased = normalized.toLowerCase()

  if (lowercased === 'true') {
    return true
  }

  if (lowercased === 'false') {
    return false
  }

  if (NUMERIC_QUERY_VALUE_REGEX.test(normalized)) {
    return Number(normalized)
  }

  return value
}

/**
 * Converts a Ghost `<popular-rvs query>` string into unified-search params.
 *
 * @param query The query attribute from a Ghost popular-rvs embed.
 * @returns Params suitable for `/api/rvlistings/unified-search`.
 *
 * @example parsePopularRvsQuery('?SearchLat=32.9&IncludeFeatured=true&FeaturedPageSize=3')
 */
export function parsePopularRvsQuery(query?: string): NonNullable<RvUnifiedSearchQuery> {
  if (!query) {
    return {}
  }

  const normalizedQuery = query.trim().replace(/^\?/, '')
  const searchParams = new URLSearchParams(normalizedQuery)
  const params: Record<string, boolean | number | string> = {}

  searchParams.forEach((value, key) => {
    if (!key || value === '') {
      return
    }

    params[key] = coercePopularRvsQueryValue(value)
  })

  return params as NonNullable<RvUnifiedSearchQuery>
}

/**
 * Extracts h2 headings from an HTML string.
 *
 * @param html The HTML content to parse.
 * @returns The h2 headings found in the HTML.
 *
 * @example getH2HeadingsFromHtml('<h2 id="intro">Introduction</h2>')
 */
export function getH2HeadingsFromHtml(html: string): GhostHeading[] {
  const headings: GhostHeading[] = []

  for (const match of html.matchAll(H2_HEADING_REGEX)) {
    const attrs = match[1] ?? ''
    const idMatch = new RegExp(HEADING_ID_ATTR_REGEX).exec(attrs)
    const text = extractHeadingText(match[2] ?? '')

    if (text) {
      const rawId = idMatch?.[1] ?? idMatch?.[2] ?? idMatch?.[3]
      headings.push({
        id: rawId ? normalizeAttributeId(rawId) : undefined,
        text,
      })
    }
  }

  return headings
}

/**
 * Converts a string to an HTML element.
 *
 * @param content The HTML content to convert to an element.
 * @returns The HTML element.
 */
export function convertToElement(content: string | Element): Element {
  if (typeof content === 'object') {
    return content
  }

  const div = document.createElement('div')
  div.innerHTML = content.trim()

  return div.firstElementChild as Element
}

/**
 * Opens links in a new window.
 *
 * @param content The HTML content to open links in.
 * @param currentPath The current path of the page.
 * @param target The target attribute to set on the links.
 * @param overwriteTarget Whether to overwrite the target attribute.
 * @returns The HTML content with the links opened in a new window.
 */
export function openLinksInNewWindow({
  content,
  currentPath,
  target = '_blank',
  overwriteTarget = false,
}: {
  content: string | Element
  currentPath: string
  target?: string
  overwriteTarget?: boolean
}): Element {
  const html = convertToElement(content)
  const links = html.querySelectorAll('a')

  links.forEach((link) => {
    if (overwriteTarget || !link.target) {
      if (link.pathname !== currentPath) {
        link.target = target
      }
    }
  })

  return html
}

/**
 * Removes tags from HTML.
 *
 * @param content The HTML content to remove tags from.
 * @param tags The tags to remove from the HTML content.
 * @returns The HTML content with the specified tags removed.
 */
export function removeTags({
  content,
  tags,
}: {
  content: string | Element
  tags: string[]
}): Element {
  const html = convertToElement(content)

  tags.forEach((tag) => html.querySelectorAll(tag).forEach((el) => el.remove()))

  return html
}
